// #genai Presentational step components extracted from SignInFlow to keep the
// orchestrator under 300 lines.
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  GUEST_ERROR_MESSAGES,
  RESEND_STATUS,
} from '../../constants/GuestAccess';
import { EV_SUPPORT_LINK } from '../../constants/AppConstants';

// ─── Loading ──────────────────────────────────────────────────────────────────

export const LoadingStep = ({ classes }) => (
  <>
    <h1 className={classes.title}>Checking link…</h1>
    <p className={classes.body}>Hang tight while we validate your access.</p>
  </>
);

LoadingStep.propTypes = { classes: PropTypes.shape().isRequired };

// ─── Not Shared (standard Okta login) ────────────────────────────────────────

export const NotSharedStep = ({ classes, onLogin, onGoBack }) => (
  <>
    <h1 className={classes.title}>Log In</h1>
    <p className={classes.body}>
      This link requires you to sign in with your EagleView account.
    </p>
    <div className={classes.form}>
      <div className={classes.actions}>
        <button type="button" className={classes.primaryBtn} onClick={onLogin}>
          Log In
        </button>
      </div>
      <div className={classes.linkRow}>
        <a className={classes.linkOld} href={EV_SUPPORT_LINK}>Need Help?</a>
        <button
          type="button"
          className={classes.linkOld}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          }}
          onClick={onGoBack}
        >
          Go Back
        </button>
      </div>
    </div>
  </>
);

NotSharedStep.propTypes = {
  classes: PropTypes.shape().isRequired,
  onLogin: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
};

// ─── Email input ──────────────────────────────────────────────────────────────

export const EmailStep = ({
  classes, email, setEmail, onContinue, isEmailValid, error,
}) => (
  <>
    <h1 className={classes.title}>Access Shared Resource</h1>
    <p className={classes.body}>Please provide your email to verify your access.</p>
    <form className={classes.form} onSubmit={onContinue}>
      <div className={classes.field}>
        <label className={classes.label} htmlFor="signin-email">
          Email
          <input
            id="signin-email"
            className={classes.input}
            type="email"
            value={email}
            placeholder="you@company.com"
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
      </div>
      <div className={classes.actions}>
        <button className={classes.primaryBtn} type="submit" disabled={!isEmailValid}>
          Continue
        </button>
      </div>
      <div className={classes.linkRow} style={{ justifyContent: 'flex-start' }}>
        <a className={classes.linkOld} href={EV_SUPPORT_LINK}>Need Help?</a>
      </div>
      <div className={classes.error}>{error}</div>
    </form>
  </>
);

EmailStep.propTypes = {
  classes: PropTypes.shape().isRequired,
  email: PropTypes.string.isRequired,
  setEmail: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  isEmailValid: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
};

// ─── Guest choice ─────────────────────────────────────────────────────────────

export const GuestChoiceStep = ({
  classes, onContinueAsGuest, sending, error, onGoBack,
}) => (
  <>
    <h1 className={classes.title}>Choose How To Access</h1>
    <p className={classes.body}>
      No account was found for this email. You can continue as a guest or create one for quicker access next time.
    </p>
    <div className={classes.form}>
      <div className={classes.actions}>
        <button
          type="button"
          className={`${classes.secondaryBtn} ${classes.guestButton}`}
          onClick={onContinueAsGuest}
          disabled={sending}
        >
          {sending ? 'Sending…' : 'Continue as guest'}
        </button>
      </div>
      <div className={classes.linkRow} style={{ justifyContent: 'flex-start', marginTop: '20px' }}>
        <span className={classes.body} style={{ marginRight: '4px' }}>Or</span>
        <button type="button" className={classes.actionLink} onClick={onGoBack}>
          Go Back
        </button>
      </div>
      <div className={classes.error}>{error}</div>
    </div>
  </>
);

GuestChoiceStep.propTypes = {
  classes: PropTypes.shape().isRequired,
  onContinueAsGuest: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  onGoBack: PropTypes.func.isRequired,
};

// ─── Verify email (polling active) ───────────────────────────────────────────

export const VerifyEmailStep = ({
  classes, isLinkExpired, countdownLabel, email, resendStatus, resendCooldownSeconds, onResend, onGoBack,
}) => {
  const isResendDisabled = resendStatus === RESEND_STATUS.RESENDING || (resendCooldownSeconds || 0) > 0;
  const cooldownMessage = (resendCooldownSeconds || 0) > 0
    ? `You can resend after ${resendCooldownSeconds} sec`
    : null;
  let resendButtonLabel = 'Resend';
  if (resendStatus === RESEND_STATUS.RESENDING) resendButtonLabel = 'Resending…';
  else if ((resendCooldownSeconds || 0) > 0) resendButtonLabel = `Resend (${resendCooldownSeconds}s)`;

  return (
    <>
      <h1 className={classes.title}>
        {isLinkExpired ? 'Link Expired' : 'Check Your Email'}
      </h1>
      <p className={classes.bodySmall}>
        {isLinkExpired ? (
          <>
            Your sign-in link has expired. Use the button below to send a new link to
            {' '}
            <strong className={classes.boldText}>{email}</strong>
            .
          </>
        ) : (
          <>
            We just sent a secure sign-in link to
            {' '}
            <strong className={classes.boldText}>{email}</strong>
            .
            {' '}
            Please check your inbox and click the link to continue.
            <br />
            Link will expire in
            {' '}
            {countdownLabel}
          </>
        )}
      </p>
      <div className={classes.form}>
        <div style={{
          marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px',
        }}
        >
          <div>
            <span className={classes.bodySmall}>Didn&apos;t get the link?</span>
            {' '}
            <button
              type="button"
              onClick={onResend}
              className={clsx(classes.actionLink, isResendDisabled && classes.actionLinkDisabled)}
              disabled={isResendDisabled}
              title={cooldownMessage || undefined}
            >
              {resendButtonLabel}
            </button>
          </div>
          <div>
            <span className={classes.bodySmall}>Or</span>
            {' '}
            <button type="button" className={classes.actionLink} onClick={onGoBack}>
              Go Back
            </button>
          </div>
        </div>
        <div className={classes.error}>
          {resendStatus === RESEND_STATUS.ERROR ? GUEST_ERROR_MESSAGES.RESEND_FAILED : ''}
        </div>
      </div>
    </>
  );
};

VerifyEmailStep.propTypes = {
  classes: PropTypes.shape().isRequired,
  isLinkExpired: PropTypes.bool.isRequired,
  countdownLabel: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  resendStatus: PropTypes.string.isRequired,
  resendCooldownSeconds: PropTypes.number,
  onResend: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
};

VerifyEmailStep.defaultProps = {
  resendCooldownSeconds: 0,
};
